/** @format */
import {ReactNode, useCallback, useMemo, useState} from 'react';

import {Box, Typography} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';

import Modal from 'components/Modal';
import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';
import MediumTextField from 'components/inputs/MediumTextField';

import {YalperPlatform} from 'graphql/generated/types';

import {useDebounceInput} from 'helpers/hooks/useDebounceInput';

import {Group} from '..';
import {SelectableList} from './SelectableList';
import {classes, StyledBox} from './styles';
import {SEARCH_DEBOUNCE_TIME} from '../constants';

export interface SensorListModalProps {
  icon: ReactNode;
  heading: string;
  list: string[];
  availableItems: string[];
  groups: Group[];
  platformsByName: ReadonlyMap<string, ReadonlyArray<YalperPlatform>>;
  onSave: (ids: Set<string>) => void;
  onClose: () => void;
}

export const SensorListModal = ({
  icon,
  list,
  heading,
  onClose,
  onSave,
  groups,
  platformsByName,
  availableItems,
}: SensorListModalProps) => {
  const {t} = useTranslation();
  const [selected, setSelected] = useState(new Set<string>());

  const {inputValue: debouncedFilter, onDebounceChange} = useDebounceInput<string>(
    '',
    SEARCH_DEBOUNCE_TIME,
  );

  const isAllSelected = useMemo(() => {
    return availableItems.every(item => selected.has(item));
  }, [availableItems, selected]);

  const handleUpdate = useCallback((item: string | Set<string>) => {
    if (typeof item === 'string') {
      setSelected(prev => {
        const updated = new Set(prev);
        if (prev.has(item)) {
          updated.delete(item);
        } else {
          updated.add(item);
        }
        return updated;
      });
    } else {
      setSelected(new Set(item));
    }
  }, []);

  const handleInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onDebounceChange(e.target.value);
    },
    [onDebounceChange],
  );

  const handleSave = () => {
    onSave(new Set(selected));
    onClose();
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(availableItems));
    }
  };

  return (
    <Modal
      header={
        <StyledBox className={classes.sensorListModalHeader}>
          {icon}
          <Typography variant='h3' sx={{fontSize: 20}}>
            <Trans>{heading}</Trans>
          </Typography>
        </StyledBox>
      }
      onClose={onClose}
    >
      <StyledBox className={classes.sensorListModalWrapper}>
        <MediumTextField
          placeholder={t('Найти сенсор', 'Найти сенсор')}
          slotProps={{
            htmlInput: {
              name: 'name',
            },
          }}
          onChange={handleInput}
        />
        <SelectableList
          selected={selected}
          list={list}
          groups={groups}
          platformsByName={platformsByName}
          filter={debouncedFilter.trim().toLowerCase()}
          onUpdate={handleUpdate}
        />
        <Box sx={{display: 'flex', gap: 2}}>
          <PrimaryMediumButton onClick={handleSave} sx={{width: 128}}>
            <Trans>ОК</Trans>
          </PrimaryMediumButton>
          <PrimaryMediumButton onClick={handleSelectAll} sx={{width: 180}}>
            <Trans>{isAllSelected ? 'Снять все' : 'Выбрать все'}</Trans>
          </PrimaryMediumButton>
        </Box>
      </StyledBox>
    </Modal>
  );
};
